<template>
  <div class="app">
    <template v-if="loadingUserProfile">
      <div class="loading-container">
        <div class="app-logo-container">
          <div class="app-logo-wrapper">
            <div class="app-logo-border"></div>
            <img class="app-logo" src="https://stage-assets.autoalert.com/img/autoalert-icons/PNG/512x512/icon-autoalert-mountains-round-ocean-deep.png" />
          </div>
        </div>
        <div class="loading-text">Loading user profile</div>
        <a class="reload-app-link" href="javascript:;" @click="reloadApp()">Click here to reload</a>
      </div>
    </template>
    <template v-else-if="User && User.UserId">
      <router-view></router-view>
      <vue-snotify></vue-snotify>
    </template>
    <div v-else class="no-user-profile-wrapper">
      <img src="https://assets.autoalert.com/img/autoalert-icons/icon-user-remove-charcoal.svg" class="no-user-profile-img" />
      <div class="no-user-profile-msg">
        <template v-if="IsImpersonatingUser && !IsImpersonationSupported">
          Impersonation detected!<br />
          CXM can't be loaded while impersonating another user.<br />
          Please dismiss impersonation or logout and login with your user
          account.<br />
          (Error code: {{ getErrorCode() }})<br /><br />
          <!-- <a class="btn no-user-profile-btn" :href="getCXMUrl()">CXM Dashboard</a><br /> -->
          <button type="button" class="btn btn-outline no-user-profile-btn" @click="dismissImpersonation()">Dismiss Impersonation</button><br />
        </template>
        <template v-if="IsEmbedded && accessTokenExpired">
          This session has expired!<br />
          Please close this window.<br />
          (Error code: {{ getErrorCode() }})
        </template>
        <template v-else>
          We're sorry, we couldn't find your user profile.<br />
          Please try again later or contact support for further assistance.<br />
          (Error code: {{ getErrorCode() }})<br /><br />
        </template>
        <button type="button" class="btn btn-outline no-user-profile-btn" @click="logoutUser" v-if="!IsEmbedded">Logout</button>
      </div>
    </div>
    <root-modals></root-modals>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import jwtDecode from "jwt-decode";
import { webAuth } from "@/auth/index";
import { HelperFunctions } from "@/lib/utilities/HelperFunctions.js";
import {
  NavMenuCategory,
  NavMenuSubcategory,
  UserSettingKeys,
} from "@/lib/utilities/enums";
import EventBus from "./lib/utilities/eventBus";
import { EventBusEvents } from "./lib/utilities/enums";

export default {
  name: "app",
  data() {
    return {
      loadingUserProfile: true,
      accessTokenExpired: false,
      accessTokenInterval: null
    };
  },
  components: {
    RootModals: () => import("@/components/modals/RootModals.vue"),
  },
  computed: {
    ...mapState({
      User: (state) => state.App.User,
      OAuthUser: (state) => state.Authentication.OAuthUser,
      IsEmbedded: (state) => state.App.IsEmbedded,
      IsImpersonatingUser: (state) => state.Authentication.IsImpersonatingUser,
      IsImpersonationSupported: (state) =>
        state.Authentication.IsImpersonationSupported,
      NavMenuFilter: (state) => state.SideNav.NavMenuFilter,
    }),
    ...mapGetters(["AccessToken", "CXMStores", "AMStores", "InboxStores"]),
    UserSettingKeys: function () {
      return UserSettingKeys;
    },
  },
  watch: {
    $route: function (newValue, oldValue) {
      if (
        newValue &&
        newValue.path &&
        (!oldValue || newValue.path != oldValue.path)
      ) {
        if (
          newValue.path &&
          newValue.path.indexOf("/app/") > -1 &&
          newValue.path.indexOf("default") < 0
        ) {
          // Update user's last viewed path
          let pathInfo = {
            path: encodeURI(newValue.path),
          };
          let data = {
            key: UserSettingKeys.LAST_VIEWED_PAGE,
            request: pathInfo,
          };
          this.$store.dispatch("saveUserSettings", data);

          // Update nav menu if needed
          let nextMenuFilter = HelperFunctions.getNavMenuFilter(
            newValue.path,
            NavMenuCategory,
            NavMenuSubcategory
          );
          if (
            this.NavMenuFilter.Category != nextMenuFilter.Category ||
            this.NavMenuFilter.Subcategory != nextMenuFilter.Subcategory
          )
            this.$store.dispatch("updateNavMenuFilter", nextMenuFilter);
        }
      }
    },
  },
  methods: {
    verifyLogin() {
      // Verify access token and get user info
      if (this.IsEmbedded && this.AccessToken) this.handleUserSetup();
      else {
        let hasToken = false;
        if (this.IsEmbedded) {
          let ssoToken = this.$route.query.ssoToken;
          if (ssoToken) {
            this.$store.dispatch("updateOAuthUser", {
              access_token: ssoToken,
            });
            this.handleUserSetup();
            hasToken = true;
          }
        }

        if (!hasToken)
          webAuth
            .getOAuthUser()
            .then((resp) => {
              this.handleUserSetup();
            })
            .catch((resp) => {
              console.log("Error verifying login status: ", resp);
            });
      }
    },
    handleUserSetup: function () {
      let vm = this;

      if (this.IsEmbedded) this.setupAccessTokenInterval();

      if (!this.IsImpersonatingUser || this.IsImpersonationSupported) {
        this.$store
          .dispatch("getCurrentUser")
          .then((resp) => {
            vm.getProductFeatures()
              .then(() => {
                if (!Array.isArray(vm.CXMStores) || vm.CXMStores.length === 0) {
                  if (vm.AMStores.length > 0) {
                    window.location = process.env.URL_ALERT_MINER;
                  } else if (vm.InboxStores.length > 0) {
                    window.location = process.env.URL_PANDO_INBOX;
                  } else {
                    window.location = process.env.PANDO_STORE;
                  }
                }

                vm.loadingUserProfile = false;
                vm.$store.dispatch("setupBaseApp");
              })
              .catch(() => {
                window.location = process.env.PANDO_STORE;
                vm.loadingUserProfile = false;
              });
          })
          .catch((resp) => {
            vm.loadingUserProfile = false;
          });
      } else {
        // Wait a few seconds to detect if impersonation has been dismissed on another Pando sub-domain
        setTimeout(() => {
          vm.loadingUserProfile = false;
        }, 2000);
      }
    },
    getErrorCode: function () {
      if (this.IsImpersonatingUser) return "0x202020";
      else if (!this.OAuthUser || this.accessTokenExpired) return "0x101010";
      else if (!this.User || !this.User.UserId) return "0x303030";
      else return "0x808080";
    },
    setupAccessTokenInterval() {
      if (this.accessTokenInterval) clearInterval(this.accessTokenInterval);

      if (!this.validateAccessToken()) return;
      this.accessTokenInterval = setInterval(this.validateAccessToken, 5000);
    },
    validateAccessToken() {
      try {
        const accessToken = jwtDecode(this.OAuthUser.access_token);
        if (accessToken && accessToken.exp) {
          const currentDateTime = Date.now().valueOf() / 1000;
          this.accessTokenExpired = accessToken.exp < currentDateTime;
        }
      } catch (err) {
        console.log("Error decoding access token: ", err);
        this.accessTokenExpired = true;
      }

      if (this.accessTokenExpired) {
        if (this.accessTokenInterval) clearInterval(this.accessTokenInterval);
      }

      return !this.accessTokenExpired;
    },
    handleLogoutNotification(notification) {
      if (!this.IsEmbedded || !notification || !this.validateAccessToken()) return;

      const accessToken = jwtDecode(this.OAuthUser.access_token);

      if (accessToken && accessToken.sub == notification.sub && accessToken.auth_time == notification.auth_time) {
        this.clearLoginInfo();
        this.validateAccessToken();
      }
    },
    clearLoginInfo() {
      this.$store.dispatch("updateOAuthUser", {
        access_token: "access_token_expired",
      });
      this.$store.dispatch("clearCurrentUser");
      this.$router.replace({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          ssoToken: "access_token_expired"
        }
      });
    },
    dismissImpersonation() {
      return webAuth.endImpersonation();
    },
    getCXMUrl() {
      return process.env.PANDO_CXM_URL;
    },
    logoutUser: function () {
      webAuth.logout(this.OAuthUser ? this.OAuthUser.access_token : null);
    },
    throwNoPermissionError: function (response) {
      let errMessage = response.ErrorCheckingPermissions
        ? "There was an error verifying your access to this customer. Please try again later."
        : "You do not have access to view this customer";
      this.$snotify.error(errMessage, "Error");
    },
    displaySnotifyNotification: function (data) {
      if (data.success) this.$snotify.success(data.message, "Success");
      else if (data.warning) this.$snotify.warning(data.message, "Warning");
      else this.$snotify.error(data.message, "Error");
    },
    getProductFeatures() {
      return Promise.all([
        this.$store.dispatch("checkUserAccountFeatures"),
        this.$store.dispatch("checkViewAccountFeatures"),
      ]).finally(() => {
        setTimeout(() => {
          this.$store.commit("featureDetailsLoaded");
        }, 300);
      });
    },
    reloadApp() {
      location.reload();
    },
  },
  created() {
    this.verifyLogin();
    EventBus.$on(EventBusEvents.EmitNoPermissionToViewContact, this.throwNoPermissionError);
    EventBus.$on(EventBusEvents.HelperFunctionSnotifyNotification, this.displaySnotifyNotification);
    EventBus.$on(EventBusEvents.UserProfileLogout, this.handleLogoutNotification);
  },
  beforeDestroy: function () {
    EventBus.$off(EventBusEvents.EmitNoPermissionToViewContact, this.throwNoPermissionError);
    EventBus.$off(EventBusEvents.HelperFunctionSnotifyNotification, this.displaySnotifyNotification);
    EventBus.$off(EventBusEvents.UserProfileLogout, this.handleLogoutNotification);

    if (this.accessTokenInterval) clearInterval(this.accessTokenInterval);
  },
};
</script>

<style lang="scss">
@import "./../styles/_vue-multiselect.scss";

.app {
  height: 100%;
  min-width: 300px;

  .loading-container {
    text-align: center;
    padding: 50px;
    position: relative;
    height: 100%;
    background: $gray-lt;

    .app-logo-container {
      text-align: center;
      padding: 50px 0px;

      .app-logo-wrapper {
        position: relative;
        display: inline-block;

        .app-logo-border {
          background: linear-gradient(
            60deg,
            #62bfff,
            #4fb1f5,
            #1f8bd5,
            #2d77ac,
            #1e4481,
            #172c55
          );
          animation: rot 1s linear infinite;
          border-style: solid;
          border-color: transparent;
          border-radius: 50%;
          border-width: 0px;
          width: 200px;
          height: 200px;
          -webkit-box-shadow: 0px 0px 5px 5px rgba(0, 0, 0, 0.18);
          -moz-box-shadow: 0px 0px 5px 5px rgba(0, 0, 0, 0.18);
          box-shadow: 0px 0px 5px 5px rgba(0, 0, 0, 0.18);
        }

        .app-logo {
          width: 190px;
          height: 190px;
          position: absolute;
          top: 5px;
          left: 5px;
        }

        @keyframes rot {
          0% {
            transform: rotate(0deg);
          }
          25% {
            transform: rotate(90deg);
          }
          50% {
            transform: rotate(180deg);
          }
          75% {
            transform: rotate(270deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      }
    }

    .loading-text {
      font-size: 25px;
    }

    .reload-app-link {
      text-decoration: none;
      position: absolute;
      bottom: 50px;
      left: 0;
      right: 0;
    }
  }

  .no-user-profile-wrapper {
    text-align: center;
    padding: 50px;
    width: 100%;
    height: 100%;
    background: $gray-lt;

    .no-user-profile-img {
      width: 100px;
      height: 100px;
      margin: 50px 0px;
    }

    .no-user-profile-msg {
      font-size: 25px;

      .no-user-profile-btn {
        margin-bottom: 15px;
        width: 200px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue